import { Component, HostListener, signal, ChangeDetectionStrategy } from '@angular/core';
import { NgClass, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { AngularNodeViewComponent } from 'ngx-tiptap';

export type ImageLayout = 'inline' | 'centered' | 'float-left' | 'float-right';

@Component({
  selector: 'mce-image-resize',
  standalone: true,
  imports: [NgClass, NgIf, MatIconModule],
  templateUrl: './image-resize.component.html',
  styleUrl: './image-resize.component.scss',
})
export class ImageResizeComponent extends AngularNodeViewComponent {

  private resizing = false;
  private startX = 0;
  private startWidth = 0;

  get src(): string {
    return this.node().attrs['src'];
  }

  get width(): string {
    const w = this.node().attrs['width'];
    return w ? `${w}` : 'auto';
  }

  get layout(): ImageLayout {
    return (this.node().attrs['layout'] as ImageLayout) ?? 'inline';
  }

  get showCaption(): boolean {
    return this.node().attrs['showCaption'] === true;
  }

  get caption(): string {
    return this.node().attrs['caption'] ?? '';
  }

  toggleCaption(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    const next = !this.showCaption;
    setTimeout(() => this.updateAttributes()({ showCaption: next }), 0);
  }

  onToolbarMouseDown(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (target?.closest('[data-drag-handle]')) {
      return;
    }

    event.preventDefault();
  }

  onCaptionInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.updateAttributes()({ caption: value });
  }

  onCaptionKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const pos = this.getPos();
    const editor = this.editor();

    if (typeof pos === 'number') {
      editor.chain().focus().setTextSelection(pos + this.node().nodeSize).run();
      return;
    }

    editor.commands.focus();
  }

  readonly aspectRatio = signal<number>(0);

  onImageLoad(event: Event) {
    const img = event.target as HTMLImageElement;
    if (img.naturalWidth > 0) {
      this.aspectRatio.set(img.naturalHeight / img.naturalWidth);
    }
  }

  setLayout(layout: ImageLayout, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    setTimeout(() => this.updateAttributes()({ layout }), 0);
  }

  onHandleMousedown(event: MouseEvent) {
    event.preventDefault();
    this.resizing = true;
    this.startX = event.clientX;

    const img = (event.target as HTMLElement).closest('.mce-image-wrapper')?.querySelector('img') as HTMLImageElement;
    this.startWidth = img?.offsetWidth ?? 200;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this.resizing) return;
    const delta = event.clientX - this.startX;
    const newWidth = Math.max(50, this.startWidth + delta);
    this.updateAttributes()({ width: `${Math.round(newWidth)}px` });
  }

  @HostListener('document:mouseup')
  onMouseUp() {
    this.resizing = false;
  }
}
